// ── 대시보드 지리/위치 유틸리티 ──────────────────────────────────────────────
// useDashboardBase에서 분리된 Geolocation + Nominatim 역지오코딩 로직
// 브라우저 위치 API로 현재 위치를 가져와 한국어 주소로 변환한다.

/**
 * 브라우저 Geolocation API로 현재 위치를 조회하고 한국어 주소로 변환
 * 상태 세터를 파라미터로 받아 useDashboardBase에서 쉽게 주입할 수 있도록 설계
 *
 * @param {{
 *   setAddressLoading:  function,
 *   setLocationUsable:  function,
 *   setCurrentAddress:  function,
 * }} setters — 상태 업데이트 함수 세트
 * @returns {Promise<string|null>} 주소 문자열, 또는 조회 실패 시 null
 */
export async function fetchCurrentAddress({ setAddressLoading, setLocationUsable, setCurrentAddress }) {
  setAddressLoading(true);
  setLocationUsable(false);

  // 브라우저 Geolocation 지원 여부 확인
  if (!navigator.geolocation) {
    setAddressLoading(false);
    setCurrentAddress('브라우저가 위치 기능을 지원하지 않습니다.');
    return null;
  }

  // 현재 위치 취득 (권한 거부 or 타임아웃 시 null)
  const position = await new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true, timeout: 6000, maximumAge: 30000,
    });
  }).catch(() => null);

  if (!position) {
    setAddressLoading(false);
    setCurrentAddress('위치 권한이 거부되었거나 현재 위치를 확인할 수 없습니다.');
    return null;
  }

  const lat = position.coords.latitude;
  const lon = position.coords.longitude;

  // Nominatim 역지오코딩 API 호출 (한국어 주소 반환)
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&accept-language=ko`
    );
    if (!response.ok) {
      const fallback = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
      setCurrentAddress(fallback); setLocationUsable(true); setAddressLoading(false);
      return fallback;
    }
    const data = await response.json();
    const text = data?.display_name || `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
    setCurrentAddress(text); setLocationUsable(true); setAddressLoading(false);
    return text;
  } catch (_error) {
    // API 호출 실패 시 위경도 좌표를 폴백으로 사용
    const fallback = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
    setCurrentAddress(fallback); setLocationUsable(true); setAddressLoading(false);
    return fallback;
  }
}
